import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Code as CodeIcon,
  Description as FileIcon,
  FolderZip as ZipIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { CodegenOutput } from '../../../services/workflow';

interface FileNode {
  id: string;
  name: string;
  type: 'file' | 'folder';
  language?: string;
  content?: string;
  children?: FileNode[];
}

interface CodegenSpecEditorProps {
  codegen: CodegenOutput | null;
  qualityScore?: number;
  qualityScoreLabel?: string;
  isLoading?: boolean;
}

const getLanguageFromFilename = (filename: string): string => {
  if (filename.endsWith('.py')) return 'python';
  if (filename.endsWith('.md')) return 'markdown';
  if (filename.endsWith('.txt')) return 'text';
  if (filename.endsWith('.json')) return 'json';
  if (filename.endsWith('.yaml') || filename.endsWith('.yml')) return 'yaml';
  return 'text';
};

const CodegenSpecEditor: React.FC<CodegenSpecEditorProps> = ({
  codegen,
  qualityScore,
  qualityScoreLabel = 'Completeness',
  isLoading = false,
}) => {
  const [tab, setTab] = useState(0);
  const [selectedFile, setSelectedFile] = useState<FileNode | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  // Build file tree from codegen output
  const fileTree = useMemo((): FileNode[] => {
    if (!codegen?.files) return [];

    const children: FileNode[] = Object.entries(codegen.files).map(
      ([name, content]) => ({
        id: name,
        name,
        type: 'file' as const,
        language: getLanguageFromFilename(name),
        content,
      })
    );

    return [
      {
        id: 'root',
        name: codegen.project_name || 'generated_agent',
        type: 'folder',
        children,
      },
    ];
  }, [codegen]);

  // Auto-select first file when codegen changes
  useEffect(() => {
    if (codegen?.files) {
      const fileNames = Object.keys(codegen.files);
      if (fileNames.length > 0) {
        // Find first Python file or fall back to first file
        const firstPythonFile = fileNames.find((name) => name.endsWith('.py'));
        const fileName = firstPythonFile || fileNames[0];
        setSelectedFile({
          id: fileName,
          name: fileName,
          type: 'file',
          language: getLanguageFromFilename(fileName),
          content: codegen.files[fileName],
        });
      }
    }
  }, [codegen]);

  // Handle file selection - get content directly from codegen
  const handleFileSelect = useCallback(
    (file: FileNode) => {
      if (
        file.type === 'file' &&
        codegen?.files &&
        file.name in codegen.files
      ) {
        setSelectedFile({
          ...file,
          content: codegen.files[file.name],
        });
      }
    },
    [codegen]
  );

  const handleCopy = useCallback(async () => {
    if (selectedFile?.content) {
      try {
        await navigator.clipboard.writeText(selectedFile.content);
        setSnackbarMessage('Copied to clipboard!');
        setSnackbarOpen(true);
      } catch (err) {
        console.error('Failed to copy:', err);
        setSnackbarMessage('Failed to copy to clipboard');
        setSnackbarOpen(true);
      }
    }
  }, [selectedFile]);

  const handleDownloadFile = useCallback(() => {
    if (selectedFile?.content && selectedFile?.name) {
      const blob = new Blob([selectedFile.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedFile.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, [selectedFile]);

  const handleDownloadZip = useCallback(async () => {
    if (!codegen?.files) return;

    setIsDownloading(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      const folder = zip.folder(codegen.project_name);
      if (folder) {
        Object.entries(codegen.files).forEach(([name, content]) => {
          folder.file(name, content);
        });
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${codegen.project_name}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSnackbarMessage('ZIP file downloaded!');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to create ZIP:', err);
      setSnackbarMessage('Failed to create ZIP file');
      setSnackbarOpen(true);
    } finally {
      setIsDownloading(false);
    }
  }, [codegen]);

  const renderFileTree = (nodes: FileNode[], depth = 0) => {
    return nodes.map((node) => (
      <React.Fragment key={node.id}>
        <ListItem
          disablePadding
          sx={{ pl: depth * 2 }}
          onClick={() => node.type === 'file' && handleFileSelect(node)}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            {node.type === 'folder' ? (
              <FolderIcon fontSize="small" color="primary" />
            ) : node.name.endsWith('.py') ? (
              <CodeIcon fontSize="small" color="secondary" />
            ) : (
              <FileIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={node.name}
            primaryTypographyProps={{
              variant: 'body2',
              fontWeight: selectedFile?.id === node.id ? 'bold' : 'normal',
              color: selectedFile?.id === node.id ? 'primary' : 'inherit',
            }}
            sx={{ cursor: node.type === 'file' ? 'pointer' : 'default' }}
          />
        </ListItem>
        {node.children && renderFileTree(node.children, depth + 1)}
      </React.Fragment>
    ));
  };

  // Empty state
  if (!codegen) {
    return (
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: 'background.default',
        }}
      >
        <CodeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          The Code Expert will generate ADK Python code based on your
          specifications.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          The generated code will appear here shortly.
        </Typography>
      </Paper>
    );
  }

  const fileCount = Object.keys(codegen.files).length;

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header with quality score */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          pt: 1,
          pb: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Generated Code
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={codegen.is_multi_agent ? 'Multi-Agent' : 'Single Agent'}
              size="small"
              color={codegen.is_multi_agent ? 'primary' : 'default'}
              variant="outlined"
            />
            <Chip
              label={`${fileCount} files`}
              size="small"
              variant="outlined"
            />
            {qualityScore !== undefined && (
              <Chip
                label={`${qualityScoreLabel}: ${Math.round(qualityScore * 100)}%`}
                size="small"
                color={
                  qualityScore >= 0.8
                    ? 'success'
                    : qualityScore >= 0.5
                      ? 'warning'
                      : 'default'
                }
                variant="outlined"
              />
            )}
          </Box>
        </Box>
        {qualityScore !== undefined && (
          <LinearProgress
            variant="determinate"
            value={qualityScore * 100}
            sx={{
              height: 4,
              borderRadius: 2,
              mb: 1,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor:
                  qualityScore >= 0.8
                    ? 'success.main'
                    : qualityScore >= 0.5
                      ? 'warning.main'
                      : 'grey.400',
              },
            }}
          />
        )}
        <Tabs
          value={tab}
          onChange={handleTabChange}
          aria-label="codegen spec tabs"
        >
          <Tab label="Code" />
          <Tab label="Details" />
        </Tabs>
      </Box>

      {/* Loading indicator */}
      {isLoading && <LinearProgress />}

      {/* Content area */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {tab === 0 ? (
          // Code tab - file tree + editor
          <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
            {/* File tree */}
            <Box
              sx={{
                width: '25%',
                borderRight: 1,
                borderColor: 'divider',
                overflow: 'auto',
                flexShrink: 0,
              }}
            >
              <Box sx={{ p: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="bold"
                >
                  PROJECT FILES
                </Typography>
              </Box>
              <List dense disablePadding>
                {renderFileTree(fileTree)}
              </List>
            </Box>

            {/* Code editor */}
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                minWidth: 0,
                height: '100%',
                overflow: 'hidden',
              }}
            >
              {/* Toolbar */}
              <Box
                sx={{
                  p: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  noWrap
                  sx={{ maxWidth: 200 }}
                >
                  {selectedFile?.name || 'Select a file'}
                </Typography>
                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                  <Button
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={handleCopy}
                    disabled={!selectedFile?.content}
                    sx={{ mr: 1 }}
                  >
                    Copy
                  </Button>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadFile}
                    disabled={!selectedFile?.content}
                    sx={{ mr: 1 }}
                  >
                    Download
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={
                      isDownloading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <ZipIcon />
                      )
                    }
                    onClick={handleDownloadZip}
                    disabled={isDownloading}
                  >
                    Download ZIP
                  </Button>
                </Box>
              </Box>

              {/* Editor */}
              <Box
                sx={{
                  flexGrow: 1,
                  minHeight: 0,
                  height: 'calc(100% - 48px)', // Subtract toolbar height
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {selectedFile ? (
                  <Editor
                    height="100%"
                    language={selectedFile.language || 'text'}
                    value={selectedFile.content || ''}
                    options={{
                      readOnly: true,
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      fontSize: 13,
                      fontFamily: "'Fira Code', 'Consolas', monospace",
                      scrollbar: {
                        vertical: 'auto',
                        horizontal: 'auto',
                      },
                    }}
                    loading={
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="100%"
                      >
                        <CircularProgress />
                      </Box>
                    }
                  />
                ) : (
                  <Box
                    sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}
                  >
                    <Typography>Select a file to view its contents</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          // Details tab - project info
          <Box sx={{ p: 2, overflow: 'auto' }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary">
                Project Information
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Project Name:</strong> {codegen.project_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong>{' '}
                  {codegen.is_multi_agent
                    ? 'Multi-Agent System'
                    : 'Single Agent'}
                </Typography>
                {codegen.agents_count && (
                  <Typography variant="body2">
                    <strong>Agents:</strong> {codegen.agents_count}
                  </Typography>
                )}
                {codegen.orchestration_pattern && (
                  <Typography variant="body2">
                    <strong>Orchestration:</strong>{' '}
                    {codegen.orchestration_pattern}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Files Generated:</strong> {fileCount}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary">
                Generated Files
              </Typography>
              <List dense>
                {Object.keys(codegen.files).map((filename) => (
                  <ListItem key={filename} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {filename.endsWith('.py') ? (
                        <CodeIcon fontSize="small" color="secondary" />
                      ) : (
                        <FileIcon fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={filename}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">
                Quick Actions
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={
                    isDownloading ? <CircularProgress size={16} /> : <ZipIcon />
                  }
                  onClick={handleDownloadZip}
                  disabled={isDownloading}
                  fullWidth
                >
                  Download All as ZIP
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default CodegenSpecEditor;
